<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Statistics from "@/services/Statistics";
import Swal from "sweetalert2";

/**
 * Best Sellers page
 */
export default {
  components: { Layout, PageHeader },
  page: {
    title: "Best Sellers",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },

  data() {
    return {
      title: "Best Sellers",
      items: [
        {
          text: "Dashboard",
          href: "/",
        },
        {
          text: "Best Sellers",
          active: true,
        },
      ],
      isBusy: false,
      tableData: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 25,
      pageOptions: [10, 25, 50, 100, 200, { value: -1, text: 'All' }],
      filter: null,
      filterOn: ['description'],
      sortBy: "total_revenue",
      sortDesc: true,
      showLoader: false,
      error: null,
      dateOption: 'monthly',
      dateRange: [],
      podProduct: false,
      fields: [
        {
          key: "product_id",
          label: "Product ID",
          sortable: true,
        },
        {
          key: "description",
          label: "Product Name",
          sortable: true,
        },
        {
          key: "total_quantity",
          label: "Quantity Sold",
          sortable: true,
        },
        {
          key: "total_revenue",
          label: "Total Revenue",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    // Calculate total revenue for all items
    totalRevenue() {
      return this.tableData.reduce((sum, item) => sum + (parseFloat(item.total_revenue) || 0), 0);
    },
    
    // Calculate total revenue for current page
    currentPageRevenue() {
      const startIndex = (this.currentPage - 1) * this.perPage;
      const endIndex = startIndex + this.perPage;
      const currentPageItems = this.tableData.slice(startIndex, endIndex);
      return currentPageItems.reduce((sum, item) => sum + (parseFloat(item.total_revenue) || 0), 0);
    },
    
    // Get pagination info
    paginationInfo() {
      const startIndex = (this.currentPage - 1) * this.perPage + 1;
      const endIndex = Math.min(this.currentPage * this.perPage, this.totalRows);
      return {
        start: startIndex,
        end: endIndex,
        total: this.totalRows
      };
    }
  },

  async mounted() {
    if (this.$route.query.dateOption) {
      this.dateOption = this.$route.query.dateOption;
    } else {
      this.dateOption = 'monthly';
    }

    this.podProduct = this.$route.query.pod_product === 'yes';

    this.updateUrlWithDateOption();
    this.setDateRange();
    await this.getBestSellers();
  },

  watch: {
    '$route.query': {
      handler(newQuery) {
        let shouldRefresh = false;

        // Handle dateOption changes
        if (newQuery.dateOption && newQuery.dateOption !== this.dateOption) {
          this.dateOption = newQuery.dateOption;
          shouldRefresh = true;
        }

        // Handle pod_product changes
        const newPodValue = newQuery.pod_product === 'yes';
        if (newPodValue !== this.podProduct) {
          this.podProduct = newPodValue;
          shouldRefresh = true;
        }

        if (shouldRefresh) {
          this.setDateRange();
          this.getBestSellers();
        }
      }
    }
  },

  methods: {
    // Update URL with current dateOption
    updateUrlWithDateOption() {
      const query = { ...this.$route.query, dateOption: this.dateOption };

      // Add pod_product parameter if checkbox is checked
      if (this.podProduct) {
        query.pod_product = 'yes';
      } else {
        // Remove pod_product parameter if checkbox is unchecked
        delete query.pod_product;
      }

      this.$router.replace({ query });
    },

    // Handle select change
    handleDateOptionChange() {
      this.updateUrlWithDateOption();
      this.setDateRange();
      this.getBestSellers();
    },

    setDateRange() {
      const today = new Date();

      switch(this.dateOption) {
        case 'today':
          this.dateRange = [today, today];
          break;
        case 'lastWeek':
          // eslint-disable-next-line no-case-declarations
          const lastWeek = new Date();
          lastWeek.setDate(today.getDate() - 7);
          this.dateRange = [lastWeek, today];
          break;
        case 'monthly':
        default:
          // eslint-disable-next-line no-case-declarations
          const lastMonth = new Date();
          lastMonth.setDate(today.getDate() - 30);
          this.dateRange = [lastMonth, today];
          break;
      }
    },

    async getBestSellers() {
      this.showLoader = true;
      this.isBusy = true;

      const startDate = this.dateRange[0];
      const endDate = this.dateRange[1];

      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const paramsObj = {
        limit: 10000,
        date_from: formatDate(startDate),
        date_to: formatDate(endDate),
      };

      // Add POD parameter if checkbox is checked
      if (this.podProduct) {
        paramsObj.pod_product = 'yes';
      }

      // Convert object to query string, skipping null values:
      const queryParams = new URLSearchParams();
      for (const key in paramsObj) {
        if (paramsObj[key] !== null && paramsObj[key] !== undefined) {
          queryParams.append(key, paramsObj[key]);
        }
      }

      const queryString = queryParams.toString();

      try {
        const response = await Statistics.getTopMerchandiseProducts(queryString);
        this.tableData = response.data.data;
        this.totalRows = this.tableData.length;
      } catch (error) {
        this.error = error.response?.data?.error || "";
        this.failedmsg(this.error);
        this.tableData = [];
        this.totalRows = 0;
      } finally {
        this.showLoader = false;
        this.isBusy = false;
      }
    },

    handleDatePickerConfirm() {
      this.getBestSellers();
    },

    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-EU', {
        style: 'currency',
        currency: 'EUR'
      }).format(value);
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-EU').format(value);
    },

    failedmsg(message) {
      Swal.fire({
        title: "Error!",
        text: message,
        icon: "error",
        confirmButtonColor: "#ff3d60",
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body" v-if="!showLoader">
            <!-- Header with filters -->
            <div class="row mb-3">
              <div class="col-sm-12 col-md-6">
                <h4 class="card-title mb-0">Best Selling Products</h4>
                <p class="text-muted">Top performing products by revenue</p>
              </div>

              <div class="col-sm-12 col-md-6">
                <div class="d-flex justify-content-end gap-2 align-items-center">
                  <!-- POD Product Checkbox -->
                  <div class="form-check">
                    <input
                        v-model="podProduct"
                        class="form-check-input"
                        type="checkbox"
                        id="podProductCheck"
                        @change="handleDateOptionChange"

                    >
                    <label class="form-check-label" for="podProductCheck">
                      POD Products
                    </label>
                  </div>

                  <!-- Date Filter -->
                  <select
                      v-model="dateOption"
                      class="form-select"
                      @change="handleDateOptionChange"
                      style="max-width: 200px;"
                  >
                    <option value="today">Today</option>
                    <option value="lastWeek">Last 7 days</option>
                    <option value="monthly">Last 30 days</option>
                  </select>

                  <!-- Refresh Button -->
<!--                  <button-->
<!--                      class="btn btn-outline-primary"-->
<!--                      @click="getBestSellers()"-->
<!--                      :disabled="isBusy"-->
<!--                  >-->
<!--                    <i class="mdi mdi-refresh" :class="{ 'mdi-spin': isBusy }"></i>-->
<!--                  </button>-->
                </div>
              </div>
            </div>

            <!-- Search -->
            <div class="row mb-3">
              <div class="col-sm-12 col-md-6">
                <div class="d-flex align-items-center">
                  <label class="me-2">Show:</label>
                  <b-form-select
                      v-model="perPage"
                      size="sm"
                      :options="pageOptions"
                      style="max-width: 100px;"
                  ></b-form-select>
                  <span class="ms-2">entries</span>
                </div>
              </div>

              <div class="col-sm-12 col-md-6">
                <div class="dataTables_filter text-md-end">
                  <label class="d-inline-flex align-items-center justify-content-end">
                    Search:
                    <b-form-input
                        v-model="filter"
                        type="search"
                        placeholder="Search products..."
                        class="form-control form-control-sm ms-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <b-table
                  :items="tableData"
                  :busy="isBusy"
                  :fields="fields"
                  responsive="sm"
                  :per-page="perPage"
                  :current-page="currentPage"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  :filter="filter"
                  :filter-included-fields="filterOn"
                  @filtered="onFiltered"
                  show-empty
                  empty-text="No best selling products found"
                  striped
                  hover
              >
                <template #table-busy>
                  <div class="text-center text-danger my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                  </div>
                </template>

                <template v-slot:cell(total_revenue)="row">
                  <span class="text-success font-weight-bold">
                    {{ formatCurrency(row.value) }}
                  </span>
                </template>

                <template v-slot:cell(total_quantity)="row">
                  <span class="text-primary">
                    {{ formatNumber(row.value) }}
                  </span>
                </template>

                <template v-slot:cell(description)="row">
                  <div class="d-flex align-items-center">
                    <div>
                      <h6 class="mb-0">{{ row.value }}</h6>
                      <!-- <small class="text-muted">ID: {{ row.item.product_id }}</small> -->
                    </div>
                  </div>
                </template>

                <template #empty>
                  <div class="text-center py-4">
                    <i class="mdi mdi-package-variant-closed font-size-48 text-muted"></i>
                    <h5 class="mt-2">No Products Found</h5>
                    <p class="text-muted">No best selling products found for the selected period.</p>
                  </div>
                </template>
              </b-table>
            </div>

            <!-- Revenue Summary -->
            <div class="row" v-if="totalRows > 0">
              <div class="col-12">
                <div class="d-flex align-items-center justify-content-end mb-3" style="background-color: #f8f9fa;">
                  <div class="revenue-summary">
                    <span class="text-muted"><strong>Total Revenue:</strong></span>
                    <span class="fw-bold text-success ms-1">
                      {{ formatCurrency(currentPageRevenue) }}
                    </span>
                    <span class="text-muted ms-1">/</span>
                    <span class="fw-bold text-primary ms-1">
                      {{ formatCurrency(totalRevenue) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div class="row" v-if="totalRows > 0">
              <div class="col-12">
                <div class="dataTables_paginate paging_simple_numbers text-end">
                  <ul class="pagination pagination-rounded mb-0 justify-content-end">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        :limit="7"
                        :hide-goto-end-buttons="false"
                        class="mb-0"
                    ></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div class="card-body d-flex align-items-center justify-content-center" v-else>
            <div class="text-center">
              <b-spinner large></b-spinner>
              <p class="mt-2 text-muted">Loading best sellers...</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.text-success {
  color: #28a745 !important;
}

.text-primary {
  color: #007bff !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

.fw-bold {
  font-weight: 700 !important;
}

.ms-1 {
  margin-left: 0.25rem !important;
}

.me-3 {
  margin-right: 1rem !important;
}

.revenue-summary {
  background-color: #f8f9fa;
  padding: 1rem 1.5rem;
  border-radius: 0.375rem;
  /* border: 1px solid #dee2e6; */
  font-size: 1.1rem;
  margin-right: 150px;
}
</style>